import { showToast, openErrorModal } from '@vario-connect/frontend-design';
import { wireTwoStepConfirm } from '../shared/listing-detail-pane.js';

// ============================================================================
// Gambio-Einstellungen - Abschnitt „Zusatzfelder verwalten".
//
// CRUD auf Gambio-Zusatzfeld-DEFINITIONEN (Shop-REST-API v2 `/additional_fields`,
// via Edge Function gambio-article-additional-catalog, actions list/create/update/delete).
// Getrennt von tabs/list-additional-fields.ts, das die WERTE je Artikel pflegt (Vario-EAV
// `gambioAdditional.f<id>_<lang>`) - hier geht es um den Katalog selbst (Feld anlegen/
// umbenennen/löschen), shop-global, nicht artikel- oder kategoriegebunden.
// ============================================================================

export interface AdditionalFieldsAdminCtx {
  supabaseUrl: string;
  session: { token: string };
}

interface CatalogField {
  fieldId: number;
  namesByLang: Record<string, string>;
}

const STOREFRONT = 'default';

function esc(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}

async function callFn<T>(ctx: AdditionalFieldsAdminCtx, body: unknown): Promise<T> {
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-article-additional-catalog`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) { const t = await res.text().catch(() => ''); throw new Error(`gambio-article-additional-catalog ${res.status}: ${t.slice(0, 200)}`); }
  return (await res.json()) as T;
}

/** de zuerst, dann alphabetisch. */
function sortLangs(a: string, b: string): number {
  if (a === b) return 0;
  if (a === 'de') return -1;
  if (b === 'de') return 1;
  return a.localeCompare(b);
}

function banner(kind: 'warn' | 'error' | 'info', msg: string): string {
  const cls = kind === 'error' ? 'v-banner-danger' : kind === 'warn' ? 'v-banner-warn' : 'v-banner-info';
  const icon = kind === 'error' ? 'fa-circle-xmark' : kind === 'warn' ? 'fa-triangle-exclamation' : 'fa-circle-info';
  return `<div class="v-pa-md"><div class="v-banner ${cls}"><span class="v-icon fa-light ${icon}"></span><div>${esc(msg)}</div></div></div>`;
}

export async function renderAdditionalFieldsAdmin(
  host: HTMLElement,
  ctx: AdditionalFieldsAdminCtx,
): Promise<void> {
  host.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Zusatzfelder…</div>`;

  let fields: CatalogField[];
  try {
    const res = await callFn<{ ok: boolean; fields: CatalogField[] }>(ctx, { action: 'list', storefront: STOREFRONT });
    fields = res.fields ?? [];
  } catch (e) {
    host.innerHTML = banner('error', `Zusatzfelder nicht ladbar: ${(e as Error).message}`);
    return;
  }

  // Sprachen aus der Union aller vorhandenen namesByLang-Keys ableiten; ohne Felder Fallback de/en.
  const langSet = new Set<string>();
  for (const f of fields) for (const l of Object.keys(f.namesByLang ?? {})) langSet.add(l.toLowerCase());
  const langs = langSet.size > 0 ? [...langSet].sort(sortLangs) : ['de', 'en'];

  const langInputRow = (lang: string, value: string, dataAttr: 'new' | 'edit'): string => `
    <div style="display:grid;grid-template-columns:44px 1fr;gap:8px;align-items:center;margin-top:4px;">
      <span class="v-text-muted" style="font-size:11px;text-transform:uppercase;">${esc(lang)}</span>
      <input class="v-input" type="text" data-${dataAttr}-lang="${esc(lang)}" placeholder="Name (${esc(lang)})" value="${esc(value)}" />
    </div>`;

  const fieldRowHtml = (f: CatalogField): string => `
    <div class="gaf-admin-row" data-field-id="${f.fieldId}" style="padding:10px 0;border-bottom:1px solid var(--go-border,#e5e7eb);">
      <div class="v-row v-row-sm" style="justify-content:space-between;align-items:center;">
        <span class="v-text-muted" style="font-size:11px;">Feld-ID ${f.fieldId}</span>
        <button type="button" class="v-button" data-gaf-delete="${f.fieldId}">
          <span class="v-icon fa-regular fa-trash"></span> Löschen
        </button>
      </div>
      ${langs.map((l) => langInputRow(l, f.namesByLang?.[l] ?? '', 'edit')).join('')}
    </div>`;

  // Zwei getrennte Blöcke mit je eigener Überschrift und eigenem Speichern-Knopf oben rechts:
  // oben das neue Feld, darunter die vorhandenen. Vorher hing der Sammel-Speichern-Knopf an der
  // Gesamtüberschrift und wirkte, als gälte er auch für das Formular darunter.
  host.innerHTML = `
    <div class="v-pa-md">
      <h4 class="klst-detail-section" style="margin:0 0 6px;">Zusatzfelder verwalten</h4>
      <p class="v-text-muted" style="font-size:12px;margin:0 0 10px;">
        Zusatzfeld-Definitionen für diesen Gambio-Shop anlegen, umbenennen oder löschen.
        Löschen entfernt das Feld shop-weit inklusive aller bisher gepflegten Werte.
      </p>
      <div class="gaf-admin-create" style="border:1px solid var(--go-border,#e5e7eb);border-radius:8px;padding:10px;margin-bottom:20px;">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin:0 0 4px;">
          <strong>Neues Zusatzfeld</strong>
          <button type="button" class="v-button v-button--save" id="gaf-admin-create-btn">
            <span class="v-icon fa-regular fa-floppy-disk"></span> Speichern
          </button>
        </div>
        ${langs.map((l) => langInputRow(l, '', 'new')).join('')}
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin:0 0 8px;">
        <h4 class="klst-detail-section" style="margin:0;">Vorhandene Zusatzfelder</h4>
        ${fields.length > 0 ? `<button type="button" class="v-button v-button--save" id="gaf-admin-save-all">
          <span class="v-icon fa-regular fa-floppy-disk"></span> Speichern
        </button>` : ''}
      </div>
      <div id="gaf-admin-rows">
        ${fields.length === 0
          ? `<div class="v-text-muted" style="font-size:13px;">Noch keine Zusatzfelder angelegt.</div>`
          : fields.map(fieldRowHtml).join('')}
      </div>
    </div>`;

  async function reload(): Promise<void> {
    await renderAdditionalFieldsAdmin(host, ctx);
  }

  host.querySelector<HTMLButtonElement>('#gaf-admin-create-btn')?.addEventListener('click', () => {
    void (async () => {
      const names: Record<string, string> = {};
      host.querySelectorAll<HTMLInputElement>('[data-new-lang]').forEach((inp) => {
        const lang = inp.dataset['newLang'];
        const v = inp.value.trim();
        if (lang && v) names[lang] = v;
      });
      if (Object.keys(names).length === 0) {
        showToast('Bitte mindestens einen Namen angeben.', 'error');
        return;
      }
      try {
        await callFn(ctx, { action: 'create', storefront: STOREFRONT, names });
        showToast('Zusatzfeld angelegt.', 'success');
        await reload();
      } catch (e) {
        openErrorModal({
          title: 'Anlegen fehlgeschlagen',
          items: [{ message: (e as Error).message }],
          context: { action: 'gambio-article-additional-catalog:create', storefront: STOREFRONT },
          json: e,
        });
      }
    })();
  });

  // Ein Button oben speichert ALLE Zusatzfeld-Definitionen (je Feld ein update-Call).
  host.querySelector<HTMLButtonElement>('#gaf-admin-save-all')?.addEventListener('click', () => {
    void (async () => {
      const rows = [...host.querySelectorAll<HTMLElement>('.gaf-admin-row')];
      try {
        for (const row of rows) {
          const fieldId = Number(row.getAttribute('data-field-id'));
          if (!Number.isFinite(fieldId)) continue;
          const names: Record<string, string> = {};
          row.querySelectorAll<HTMLInputElement>('[data-edit-lang]').forEach((inp) => {
            const lang = inp.dataset['editLang'];
            const v = inp.value.trim();
            if (lang && v) names[lang] = v;
          });
          await callFn(ctx, { action: 'update', storefront: STOREFRONT, fieldId, names });
        }
        showToast('Zusatzfelder gespeichert.', 'success');
        await reload();
      } catch (e) {
        openErrorModal({
          title: 'Speichern fehlgeschlagen',
          items: [{ message: (e as Error).message }],
          context: { action: 'gambio-article-additional-catalog:update', storefront: STOREFRONT },
          json: e,
        });
      }
    })();
  });

  host.querySelectorAll<HTMLButtonElement>('[data-gaf-delete]').forEach((btn) => {
    const fieldId = Number(btn.getAttribute('data-gaf-delete'));
    wireTwoStepConfirm(
      btn,
      '<span class="v-icon fa-regular fa-triangle-exclamation"></span> Wirklich löschen? entfernt alle Werte shop-weit',
      () => {
        void (async () => {
          try {
            await callFn(ctx, { action: 'delete', storefront: STOREFRONT, fieldId });
            showToast('Zusatzfeld gelöscht.', 'success');
            await reload();
          } catch (e) {
            openErrorModal({
              title: 'Löschen fehlgeschlagen',
              items: [{ message: (e as Error).message }],
              context: { action: 'gambio-article-additional-catalog:delete', storefront: STOREFRONT, fieldId },
              json: e,
            });
          }
        })();
      },
    );
  });
}
